import React, { createContext, useState, useContext, useEffect } from 'react';
import api from '../services/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    const storedToken = localStorage.getItem('token');
    if (storedUser && storedToken) {
      setUser(JSON.parse(storedUser));
      api.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
    }
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    try {
      const { data } = await api.post('/login', { email, password });
      setUser(data.user);
      localStorage.setItem('user', JSON.stringify(data.user));
      localStorage.setItem('token', data.token);
      api.defaults.headers.common['Authorization'] = `Bearer ${data.token}`;
      if (data.needs_payment) {
        return { success: true, needsPayment: true, message: data.message };
      }
      return { success: true };
    } catch (error) {
      const msg =
        error.response?.data?.message ||
        error.response?.data?.errors?.email?.[0] ||
        'Email ou mot de passe incorrect';
      return { success: false, error: msg };
    }
  };

  const register = async (formData) => {
    try {
      const { data } = await api.post('/register', formData);
      setUser(data.user);
      localStorage.setItem('user', JSON.stringify(data.user));
      localStorage.setItem('token', data.token);
      api.defaults.headers.common['Authorization'] = `Bearer ${data.token}`;
      if (data.needs_payment) {
        return { success: true, needsPayment: true };
      }
      return { success: true };
    } catch (error) {
      const errors = error.response?.data?.errors;
      if (errors) {
        const allMessages = Object.values(errors).flat();
        return { success: false, error: allMessages.join('\n'), errors: allMessages };
      }
      return { success: false, error: error.response?.data?.message || 'Erreur lors de l\'inscription.', errors: [] };
    }
  };

  const logout = async () => {
    try {
      await api.post('/logout');
    } catch (_) {}
    setUser(null);
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    delete api.defaults.headers.common['Authorization'];
  };

  const resetPassword = async (email) => {
    try {
      const { data } = await api.post('/forgot-password', { email });
      return { success: true, message: data.message };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Erreur lors de la réinitialisation' };
    }
  };

  const confirmResetPassword = async (token, email, password, passwordConfirmation) => {
    try {
      const { data } = await api.post('/reset-password', {
        token,
        email,
        password,
        password_confirmation: passwordConfirmation,
      });
      return { success: true, message: data.message };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Erreur lors de la réinitialisation',
      };
    }
  };

  const updateUser = (newData) => {
    const updated = { ...user, ...newData };
    setUser(updated);
    localStorage.setItem('user', JSON.stringify(updated));
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    resetPassword,
    confirmResetPassword,
    updateUser,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
